/**
 * Single source of truth for the computer-use permission default.
 *
 * Every other plugin skill is default-enabled: `isPluginSkillPermissionEnabled`
 * treats an absent entry as `true`, which is right for the read-mostly
 * connectors. Computer use grants an agent unrestricted screen capture and
 * mouse/keyboard injection on the user's machine, so it inverts that default —
 * absent means off, and only an explicit `true` enables it.
 *
 * Server and web both resolve the toggle through this module. If the web UI
 * derived the default independently it would render the toggle "on" while the
 * server treated it as off, telling the user an agent cannot control their
 * machine when it can (or the reverse). One function, so the two cannot drift.
 */

export const COMPUTER_USE_PLUGIN_ID = "computer-use";
export const COMPUTER_USE_SKILL_ID = "computer-use";
export const COMPUTER_USE_TOOL_PREFIX = "forge_computer_";

/** Plugin skill permission map, structurally shared by server settings and web state. */
type PluginSkillPermissionMap = Readonly<Record<string, Readonly<Record<string, boolean>>>>;

export function isComputerUseSkill(pluginId: string, skillId: string): boolean {
  return pluginId === COMPUTER_USE_PLUGIN_ID && skillId === COMPUTER_USE_SKILL_ID;
}

export function isComputerUseToolName(toolName: string): boolean {
  return toolName.startsWith(COMPUTER_USE_TOOL_PREFIX);
}

export function isComputerUseEnabledIn(permissions: PluginSkillPermissionMap): boolean {
  return permissions[COMPUTER_USE_PLUGIN_ID]?.[COMPUTER_USE_SKILL_ID] === true;
}

/**
 * Resolves the effective enabled state for any plugin skill, applying the
 * opt-in inversion for computer use and the default-enabled rule for
 * everything else. Callers should prefer this over reading the record directly.
 */
export function resolvePluginSkillEnabled(
  permissions: PluginSkillPermissionMap,
  pluginId: string,
  skillId: string,
): boolean {
  return isComputerUseSkill(pluginId, skillId)
    ? isComputerUseEnabledIn(permissions)
    : permissions[pluginId]?.[skillId] !== false;
}
